import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as Logo } from 'images/logo.svg';

// Scrim sobre la foto de fondo: doble gradiente (lateral para desktop, vertical para el
// apilado de móvil) que sostiene el contraste del titular blanco pase lo que pase con la
// imagen. Va delante de la url en el shorthand `background`, así que queda encima.
const PHOTO_SCRIM =
  'linear-gradient(90deg, rgba(9, 14, 22, 0.94) 0%, rgba(9, 14, 22, 0.78) 48%, rgba(9, 14, 22, 0.44) 100%), ' +
  'linear-gradient(0deg, rgba(9, 14, 22, 0.72) 0%, rgba(9, 14, 22, 0.20) 60%)';

/**
 * Encabezado del dashboard estilo Spotify. Mode-aware: en oscuro gradiente off-black +
 * titular blanco; en claro gradiente claro + titular navy. El dorado solo aparece como
 * acento pequeño en el eyebrow. Watermark del logo OTRIX difuminado en la esquina (en
 * oscuro va invertido a blanco; en claro a color, vía `brand.logoFilter`).
 *
 * Con `backgroundImage` la banda pasa a ser una foto con scrim (empresa cliente). Sin ella cae
 * al gradiente de marca de siempre, que es lo que ve todo el mundo.
 *
 * `logoImage` se pinta ENCIMA y CONTENIDO (nunca recortado ni deformado): un logo es una marca,
 * no una textura de fondo, y estirarlo o cortarlo lo destroza. Por eso son dos props y no una.
 */
function DashboardHero({
  backgroundImage = null,
  logoImage = null,
  eyebrow = null,
  greeting,
  highlight = null,
  subtitle = null,
  statusChip = null
}) {
  const theme = useTheme();

  const logoWatermarkStyles = {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    opacity: 0.05,
    filter: theme.palette.brand.heroLogoFilter,
    transform: 'translateX(32%)',
    pointerEvents: 'none'
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        p: { xs: 3, md: 4 },
        border: '1px solid',
        borderColor: theme.palette.brand.heroBorder,
        background: backgroundImage
          ? `${PHOTO_SCRIM}, url("${backgroundImage}") center / cover no-repeat`
          : theme.palette.brand.heroGradient,
        ...(backgroundImage && { minHeight: { xs: 200, md: 240 }, display: 'flex', alignItems: 'flex-end' }),
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 10px 30px rgba(0, 0, 0, 0.35)'
      }}>
      <Logo aria-hidden style={logoWatermarkStyles} />

      <Stack spacing={1.25} sx={{ position: 'relative', width: '100%' }}>
        {/* Logo de la empresa: `contain` y alturas fijas por breakpoint para que se lea igual
            en un móvil de 375px que en desktop. La placa translúcida detrás lo despega de la
            foto (un logo oscuro sobre una foto oscura desaparecería). */}
        {logoImage && (
          <Box
            sx={{
              alignSelf: 'flex-start',
              maxWidth: { xs: 140, md: 190 },
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.10)',
              border: '1px solid rgba(255, 255, 255, 0.16)'
            }}>
            <Box
              component="img"
              src={logoImage}
              alt=""
              aria-hidden
              sx={{
                display: 'block',
                height: { xs: 30, md: 40 },
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {eyebrow && (
            <Chip
              label={eyebrow}
              size="small"
              sx={{
                // El hero es banda navy en ambos modos: el eyebrow va en dorado BRILLANTE.
                backgroundColor: 'rgba(249, 189, 78, 0.16)',
                color: 'gold.bright',
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                fontSize: '0.62rem',
                height: 22,
                borderRadius: '999px'
              }}
            />
          )}
          {statusChip}
        </Stack>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: theme.palette.brand.heroText,
            textWrap: 'balance',
            fontSize: { xs: '1.8rem', md: '2.4rem' }
          }}>
          {greeting}
          {highlight && ` ${highlight}`}
        </Typography>

        {subtitle && (
          <Typography variant="body1" sx={{ color: theme.palette.brand.heroSubtext, maxWidth: 560 }}>
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

DashboardHero.propTypes = {
  backgroundImage: PropTypes.string,
  /** Logo de la empresa: se pinta contenido sobre la banda, nunca como fondo. */
  logoImage: PropTypes.string,
  eyebrow: PropTypes.string,
  greeting: PropTypes.string.isRequired,
  highlight: PropTypes.string,
  subtitle: PropTypes.string,
  statusChip: PropTypes.node
};

export default DashboardHero;
